import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ConsultationForm } from '../components/ConsultationForm';
import { FileText, Scale, AlertTriangle, Shield, Users, DollarSign, Lock } from 'lucide-react';

export const TermsConditions = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  const openForm = () => {
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar onOpenForm={openForm} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#0A1F44] to-[#153356]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-2 mb-6">
              <Scale className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-medium">Legal Agreement</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Terms and Conditions
            </h1>
            <p className="text-xl text-gray-300">
              Last Updated: January 5, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Introduction */}
          <div className="mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Welcome to Otis Trademark. These Terms and Conditions ("Terms", "Agreement") govern your access to and use of our website, services, and legal representation. By accessing or using our services, you agree to be bound by these Terms.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <strong>Important:</strong> Please read these Terms carefully before using our services. If you do not agree with any part of these Terms, you must not use our website or services. Your continued use of our services constitutes acceptance of these Terms and any future modifications.
            </p>
          </div>

          {/* 1. Acceptance of Terms */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <FileText className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">1. Acceptance of Terms</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              By accessing, browsing, or using the Otis Trademark website and services, you acknowledge that:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9">
              <li>You have read, understood, and agree to be bound by these Terms and Conditions</li>
              <li>You have read and agree to our Privacy Policy, which is incorporated into these Terms by reference</li>
              <li>You are at least 18 years of age and have the legal capacity to enter into binding agreements</li>
              <li>If you are accessing services on behalf of a business entity, you have the authority to bind that entity to these Terms</li>
              <li>You will comply with all applicable local, state, federal, and international laws and regulations</li>
            </ul>
          </div>

          {/* 2. Services Provided */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Scale className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">2. Services Provided</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              Otis Trademark is a law firm that provides intellectual property legal services, including but not limited to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9 mb-4">
              <li>Trademark search, clearance, and registration</li>
              <li>Trademark monitoring, renewal, and maintenance</li>
              <li>Copyright registration and protection</li>
              <li>Patent search and filing services</li>
              <li>Business entity formation and compliance</li>
              <li>Intellectual property consultation and legal advice</li>
              <li>Opposition, cancellation, and enforcement proceedings</li>
            </ul>
            <p className="text-gray-700 leading-relaxed ml-9">
              <strong>Note:</strong> All legal services are provided by licensed attorneys admitted to practice in their respective jurisdictions. The specific services available to you may vary based on your jurisdiction and the nature of your legal matter.
            </p>
          </div>

          {/* 3. Attorney-Client Relationship */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Users className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">3. Attorney-Client Relationship</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              <strong>Formation of Relationship:</strong> An attorney-client relationship is formed only when:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9 mb-4">
              <li>You retain our services through a signed engagement agreement</li>
              <li>Payment for services has been received or payment arrangements have been made</li>
              <li>A licensed attorney from Otis Trademark has agreed to represent you</li>
              <li>You receive written confirmation of our representation</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4 ml-9 bg-blue-50 border-l-4 border-blue-400 p-4">
              <strong>Important:</strong> Browsing our website, reading our content, or submitting a consultation request does not create an attorney-client relationship. No attorney-client relationship exists until you receive express written confirmation of our representation.
            </p>
            <p className="text-gray-700 leading-relaxed ml-9">
              <strong>Confidentiality:</strong> Once an attorney-client relationship is established, all communications and information you provide are protected by attorney-client privilege and will be kept strictly confidential in accordance with professional ethics rules and applicable law.
            </p>
          </div>

          {/* 4. User Accounts and Responsibilities */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Lock className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">4. User Accounts and Responsibilities</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 ml-9">4.1 Account Security</h3>
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              When you create an account with Otis Trademark, you agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9 mb-6">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the confidentiality of your password and account credentials</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized access or security breach</li>
              <li>Not share your account credentials with any third party</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 ml-9">4.2 Account Responsibility</h3>
            <p className="text-gray-700 leading-relaxed ml-9 bg-red-50 border-l-4 border-red-400 p-4">
              <strong>You are wholly responsible for any access to our website and services by individuals using your User ID and password.</strong> You must notify Otis Trademark immediately if you believe your User ID is being used maliciously or without your authorization. We are not liable for any loss or damage arising from your failure to maintain account security.
            </p>
          </div>

          {/* 5. User Content and Conduct */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">5. User-Generated Content and Conduct</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 ml-9">5.1 License to User Content</h3>
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              When you submit content, information, documents, or materials to Otis Trademark (collectively, "User Content"), you grant us the right to use, reproduce, modify, and distribute such content for the purpose of providing our services and representing your interests. This includes authorization to publish or file your content with government agencies, third parties, and other entities as necessary for your legal representation.
            </p>

            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 mt-6 ml-9">5.2 Prohibited Content and Conduct</h3>
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              You agree not to post, submit, or use any User Content or engage in conduct that:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9">
              <li>Infringes upon the intellectual property rights of others (including copyrights, trademarks, patents, or trade secrets)</li>
              <li>Violates the privacy rights, publicity rights, or other legal rights of third parties</li>
              <li>Is unlawful, fraudulent, deceptive, inaccurate, or misleading</li>
              <li>Contains defamatory, libelous, discriminatory, hateful, or harassing content</li>
              <li>Is pornographic, obscene, indecent, or sexually explicit</li>
              <li>Contains threats, promotes violence, or advocates harm to others</li>
              <li>Promotes illegal activities or violates any laws or regulations</li>
              <li>Contains viruses, malware, or other harmful computer code</li>
              <li>Attempts to gain unauthorized access to our systems or other users' accounts</li>
              <li>Interferes with the proper functioning of our website or services</li>
              <li>Damages, disparages, or harms the reputation of Otis Trademark, our affiliates, or our clients</li>
              <li>Impersonates any person or entity or misrepresents your affiliation</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 mt-6 ml-9">5.3 Content Moderation</h3>
            <p className="text-gray-700 leading-relaxed ml-9">
              Otis Trademark reserves the right, but is not obligated, to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9 mt-2">
              <li>Monitor, review, and moderate User Content</li>
              <li>Remove or refuse to publish any User Content that violates these Terms</li>
              <li>Suspend or terminate user accounts for violations</li>
              <li>Take legal action against users who violate these Terms</li>
              <li>Cooperate with law enforcement authorities regarding illegal activities</li>
            </ul>
          </div>

          {/* 6. Fees and Payment Terms */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <DollarSign className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">6. Fees and Payment Terms</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 ml-9">6.1 Service Fees</h3>
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              Our service fees are clearly stated on our website and in your engagement agreement. Fees may include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9 mb-4">
              <li>Attorney fees for legal services</li>
              <li>Government filing fees (USPTO, Copyright Office, state agencies)</li>
              <li>Search and research fees</li>
              <li>Monitoring and maintenance fees</li>
              <li>Third-party service provider fees</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 ml-9">6.2 Payment Obligations</h3>
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              By engaging our services, you agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9">
              <li>Pay all fees as specified in your engagement agreement</li>
              <li>Provide valid payment information and keep it current</li>
              <li>Authorize us to charge your payment method for services rendered</li>
              <li>Pay all applicable taxes, if any</li>
              <li>Reimburse us for any expenses incurred on your behalf</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 mt-6 ml-9">6.3 Refund Policy</h3>
            <p className="text-gray-700 leading-relaxed ml-9">
              <strong>Attorney Fees:</strong> Once legal work has commenced, attorney fees are generally non-refundable. Refund eligibility depends on the specific services provided and the stage of your matter.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3 ml-9">
              <strong>Government Filing Fees:</strong> Government filing fees paid to the USPTO, Copyright Office, or other agencies are non-refundable once your application has been submitted.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3 ml-9">
              <strong>Refund Requests:</strong> All refund requests must be submitted in writing and will be evaluated on a case-by-case basis in accordance with our engagement agreement and professional ethics obligations.
            </p>
          </div>

          {/* 7. Intellectual Property Rights */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Shield className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">7. Intellectual Property Rights</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 ml-9">7.1 Our Intellectual Property</h3>
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              All content on the Otis Trademark website, including but not limited to text, graphics, logos, images, software, and design elements, is the property of Otis Trademark or our licensors and is protected by U.S. and international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              You may not copy, reproduce, distribute, modify, create derivative works, publicly display, or exploit any content from our website without our express written permission.
            </p>

            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 ml-9">7.2 Your Intellectual Property</h3>
            <p className="text-gray-700 leading-relaxed ml-9">
              You retain all ownership rights to your intellectual property. By engaging our services, you grant us a limited license to use your intellectual property solely for the purpose of providing legal representation and services as outlined in your engagement agreement.
            </p>
          </div>

          {/* 8. Disclaimer of Warranties */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">8. Disclaimer of Warranties</h2>
            </div>
            
            <div className="text-gray-700 leading-relaxed ml-9 bg-red-50 border-l-4 border-red-400 p-4">
              <p className="mb-4">
                <strong>OUR WEBSITE AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.</strong>
              </p>
              <p className="mb-4">
                TO THE FULLEST EXTENT PERMITTED BY LAW, OTIS TRADEMARK DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4">
                <li>MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT</li>
                <li>ACCURACY, COMPLETENESS, OR RELIABILITY OF INFORMATION</li>
                <li>UNINTERRUPTED, SECURE, OR ERROR-FREE OPERATION OF OUR WEBSITE</li>
                <li>FREEDOM FROM VIRUSES OR OTHER HARMFUL COMPONENTS</li>
              </ul>
              <p>
                WE DO NOT GUARANTEE ANY SPECIFIC LEGAL OUTCOME OR RESULTS. LEGAL PROCEEDINGS INVOLVE INHERENT UNCERTAINTIES, AND PAST RESULTS DO NOT GUARANTEE FUTURE SUCCESS.
              </p>
            </div>
          </div>

          {/* 9. Limitation of Liability */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Scale className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">9. Limitation of Liability</h2>
            </div>
            
            <div className="text-gray-700 leading-relaxed ml-9 bg-red-50 border-l-4 border-red-400 p-4">
              <p className="mb-4">
                <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, OTIS TRADEMARK AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
                <li>LOSS OF PROFITS, REVENUE, DATA, OR BUSINESS OPPORTUNITIES</li>
                <li>DAMAGES ARISING FROM USE OR INABILITY TO USE OUR SERVICES</li>
                <li>UNAUTHORIZED ACCESS TO OR ALTERATION OF YOUR DATA</li>
                <li>THIRD-PARTY CONDUCT OR CONTENT</li>
                <li>ERRORS, MISTAKES, OR INACCURACIES IN CONTENT</li>
              </ul>
              <p className="mt-4">
                OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO OUR SERVICES SHALL NOT EXCEED THE AMOUNT YOU PAID TO OTIS TRADEMARK IN THE 12 MONTHS PRECEDING THE CLAIM.
              </p>
            </div>
          </div>

          {/* 10. Indemnification */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Shield className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">10. Indemnification</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed ml-9">
              You agree to indemnify, defend, and hold harmless Otis Trademark and its officers, directors, employees, agents, affiliates, and licensors from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from or related to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9 mt-4">
              <li>Your use of our website or services</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another person or entity</li>
              <li>Your User Content or conduct</li>
              <li>Your negligence, willful misconduct, or illegal activities</li>
            </ul>
          </div>

          {/* 11. Assignment and Transfer */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <FileText className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">11. Assignment and Transfer</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed ml-9">
              You may not assign, transfer, or delegate your rights and obligations under these Terms or your engagement agreement to any third party without Otis Trademark's prior written consent. Any attempted assignment without consent is void.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4 ml-9">
              Otis Trademark may assign or transfer these Terms and our obligations to an affiliate, successor, or purchaser of our business without restriction.
            </p>
          </div>

          {/* 12. Termination */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">12. Termination</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 ml-9">12.1 Termination by You</h3>
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              You may terminate your use of our website at any time by ceasing to access our services. To terminate an attorney-client relationship, you must provide written notice in accordance with your engagement agreement.
            </p>

            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 ml-9">12.2 Termination by Us</h3>
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              We reserve the right to suspend or terminate your access to our services at any time, with or without cause, and with or without notice, for any reason including but not limited to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-9">
              <li>Violation of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Non-payment of fees</li>
              <li>Abusive or threatening conduct</li>
              <li>Conflicts of interest</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0A1F44] mb-3 mt-6 ml-9">12.3 Effects of Termination</h3>
            <p className="text-gray-700 leading-relaxed ml-9">
              Upon termination of your access to our services, your right to use our website will immediately cease. Provisions of these Terms that by their nature should survive termination (including payment obligations, indemnification, limitation of liability, and dispute resolution) shall remain in effect. We may retain your information as required by law or for legitimate business purposes.
            </p>
          </div>

          {/* 13. Governing Law and Dispute Resolution */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <Scale className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">13. Governing Law and Dispute Resolution</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              These Terms shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law provisions.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              Any disputes arising from or relating to these Terms or our services shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, conducted in New York, NY. You agree to waive your right to a jury trial or to participate in a class action lawsuit.
            </p>
            <p className="text-gray-700 leading-relaxed ml-9">
              Notwithstanding the foregoing, either party may seek injunctive relief in any court of competent jurisdiction to protect intellectual property rights or confidential information.
            </p>
          </div>

          {/* 14. Changes to Terms */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <FileText className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">14. Changes to Terms and Conditions</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-4 ml-9">
              We reserve the right to modify these Terms at any time in our sole discretion. Changes will be effective immediately upon posting to our website with an updated "Last Updated" date.
            </p>
            <p className="text-gray-700 leading-relaxed ml-9">
              Your continued use of our services after changes are posted constitutes your acceptance of the modified Terms. If you do not agree to the changes, you must discontinue use of our services. Material changes affecting existing attorney-client relationships will be communicated directly to affected clients.
            </p>
          </div>

          {/* 15. Miscellaneous */}
          <div className="mb-12">
            <div className="flex items-start space-x-3 mb-4">
              <FileText className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-[#0A1F44]">15. Miscellaneous Provisions</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-3 ml-9">
              <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy and any engagement agreement, constitute the entire agreement between you and Otis Trademark regarding use of our services.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3 ml-9">
              <strong>Severability:</strong> If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3 ml-9">
              <strong>Waiver:</strong> No waiver of any term shall be deemed a further or continuing waiver of such term or any other term.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3 ml-9">
              <strong>Force Majeure:</strong> We shall not be liable for any failure to perform due to circumstances beyond our reasonable control.
            </p>
            <p className="text-gray-700 leading-relaxed ml-9">
              <strong>Headings:</strong> Section headings are for convenience only and shall not affect the interpretation of these Terms.
            </p>
          </div>

          {/* Contact Information */}
          <div className="bg-gradient-to-br from-[#0A1F44] to-[#153356] rounded-2xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-4">Questions About These Terms?</h2>
            <p className="text-gray-300 mb-6">
              If you have questions or concerns regarding these Terms and Conditions, please contact us:
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <FileText className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <div className="font-semibold">Email:</div>
                  <a href="mailto:legal@otistrademark.com" className="text-[#D4AF37] hover:underline">
                    legal@otistrademark.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <FileText className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <div className="font-semibold">Mailing Address:</div>
                  <div className="text-gray-300">
                    Otis Trademark<br />
                    Legal Department<br />
                    1140 Harrison St<br />
                    San Francisco, CA 94103
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <Footer />
      <ConsultationForm isOpen={isFormOpen} onClose={closeForm} formType="terms" />
    </div>
  );
};
